import {useEffect, useMemo, useRef} from 'react';
import {NativeId} from '@packages/systems/core/utils/NativeIdUtils';
import {getNativeNodeByNativeIdPath} from '@packages/systems/designer/canvas';
import {
  getDesignerAppOrigin,
  getAnonymousCommentsAppOrigin,
} from '@packages/utilities/canvas-origin';
type ResizeMessage = {
  action: 'observeResize' | 'unobserveResize';
  nativeIdPath: NativeId[];
};

// This hook listens for observeResize and unobserveResize actions via postMessage,
// sets up ResizeObserver instances for DOM nodes identified by their nativeIdPath,
// and sends resize notifications back to the Designer

export const useResizeObserver = () => {
  // Track mapping of observed nodes to their nativeIdPath
  const nodeToNativeIdPathMap = useRef(new Map<Element, NativeId[]>());

  const designerAppOrigin = getDesignerAppOrigin(window.location.origin);
  const anonymousCommentsAppOrigin = getAnonymousCommentsAppOrigin(
    window.location.origin
  );

  // Create a single ResizeObserver instance
  const observer = useMemo(
    () =>
      new ResizeObserver((entries) => {
        const nativeIdPaths: NativeId[][] = [];

        entries.forEach((entry) => {
          const node = entry.target;
          const nativeIdPath = nodeToNativeIdPathMap.current.get(node);
          if (nativeIdPath) {
            nativeIdPaths.push(nativeIdPath);
          }
        });

        if (nativeIdPaths.length > 0 && designerAppOrigin) {
          window.parent.postMessage(
            {
              type: 'resizeNotification',
              nativeIdPaths,
            },
            designerAppOrigin
          );
        }
      }),
    []
  );

  useEffect(() => {
    const handleMessage = (event: MessageEvent<ResizeMessage>) => {
      if (!designerAppOrigin || !anonymousCommentsAppOrigin) {
        return;
      }

      if (
        event.origin !== designerAppOrigin &&
        event.origin !== anonymousCommentsAppOrigin
      ) {
        return;
      }

      const {action, nativeIdPath} = event.data;

      if (action === 'observeResize') {
        // Find the node that matches this nativeIdPath
        const node = getNativeNodeByNativeIdPath(nativeIdPath);
        if (node && !nodeToNativeIdPathMap.current.has(node)) {
          nodeToNativeIdPathMap.current.set(node, nativeIdPath);
          observer.observe(node);
        }
      } else if (action === 'unobserveResize') {
        const node = getNativeNodeByNativeIdPath(nativeIdPath);
        if (node) {
          nodeToNativeIdPathMap.current.delete(node);
          observer.unobserve(node);
        }
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
      observer.disconnect();
      nodeToNativeIdPathMap.current.clear(); // Cleanup
    };
  }, [observer]);
};
